import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { PurchaseCostDialog } from 'src/dialogs/PurchaseCostDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { CostDetailsTable } from 'src/modals/common/Purchase/CostDetailsTable/CostDetailsTable';
import { PurchaseOrderFormDataType } from 'src/modals/MultiplePurchaseWizard/MultiplePurchaseWizard';
import { newBigIntId } from 'src/utils/idUtils';
import { posChangedField } from 'src/utils/posFieldUtils';
import {
  CostDetail,
  PosFieldArrayOfCostDetail,
  PurchaseOrderCostType,
  TicketGroupInput,
} from 'src/WebApiController';

export const useCostDetailsInput = ({
  ticketGroups,
  additionalCosts,
  currency,
  index,
}: {
  ticketGroups: TicketGroupInput[];
  additionalCosts: PosFieldArrayOfCostDetail | null;
  currency: string;
  index: number;
}) => {
  const { getValues, setValue, formState } =
    useFormContext<PurchaseOrderFormDataType>();
  const [currentCostModel, setCurrentCostModel] = useState<CostDetail>();
  const costDialog = useBasicDialog();

  const onAddNewCost = useCallback(() => {
    setCurrentCostModel({
      id: newBigIntId(),
      cost: null,
      costType: PurchaseOrderCostType.Tax,
      date: new Date().toISOString(),
      description: null,
    } as CostDetail);

    costDialog.launchDialog();
  }, [costDialog, setCurrentCostModel]);

  const onEditCost = useCallback(
    (cost: CostDetail) => {
      setCurrentCostModel(cost);

      costDialog.launchDialog();
    },
    [costDialog]
  );

  const onRemoveCost = useCallback(
    (costId: number) => {
      const newCosts = additionalCosts?.value?.filter((p) => p.id !== costId);
      if (newCosts && newCosts?.length !== additionalCosts?.value?.length) {
        setValue(
          `purchaseOrders.${index}.additionalCosts`,
          posChangedField(newCosts)
        );
      }
    },
    [additionalCosts, setValue]
  );

  const onAddNewCostSave = useCallback(
    (cost: CostDetail) => {
      const existingCost = additionalCosts?.value?.find(
        (p) => p.id === cost.id
      );
      if (existingCost) {
        existingCost.cost = cost.cost;
        existingCost.costType = cost.costType;
        existingCost.date = cost.date;
        existingCost.description = cost.description;

        setValue(
          `purchaseOrders.${index}.additionalCosts`,
          posChangedField(additionalCosts!.value)
        );
      } else {
        setValue(
          `purchaseOrders.${index}.additionalCosts`,
          posChangedField([...(additionalCosts?.value ?? []), cost])
        );
      }
      costDialog.closeDialog();
    },
    [additionalCosts, costDialog, setValue]
  );

  const onAddNewCostCancel = useCallback(() => {
    setCurrentCostModel(undefined);
    costDialog.closeDialog();
  }, [costDialog]);

  const purchaseCostDialog = costDialog.dialogProps.isOpen && (
    <PurchaseCostDialog
      {...costDialog.dialogProps}
      cost={currentCostModel!}
      currencyCode={currency}
      onSave={onAddNewCostSave}
      onClosed={onAddNewCostCancel}
    />
  );

  const costDetailsTable = (
    <CostDetailsTable
      currencyCode={currency}
      ticketGroups={ticketGroups}
      costs={additionalCosts?.value}
      onEditCost={onEditCost}
      onAddNewCost={onAddNewCost}
      onRemoveCost={onRemoveCost}
    />
  );

  return { purchaseCostDialog, costDetailsTable };
};
